import React, { useState, useEffect } from 'react';
import { MovelContratacao, validarMovel } from '@/components/venda/MovelContratacao';
import { TvPontosAdicionais, InternetsAdicionais, totalInternetsAdicionais, planosExtrasResumo, validarPlanosExtras, temTiposPontoAdicional } from '@/components/venda/PlanosExtras';
import { valorPorPagamento, labelPlano, aplicarFormaPagamento } from '@/components/venda/valorPagamento';
import { useAuth } from '@/contexts/AuthContext';
import { useJornada } from '@/contexts/JornadaContext';
import { useLocation } from 'react-router-dom';
import { vendasAPI, empresasAPI, produtosAPI, cepAPI, valoresAPI, formatarErroAPI, formatarTelefoneBR } from '@/lib/api';
import api from '@/lib/api';
import { TurnoToggle } from '@/components/PrevisoesInstalacao';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Save, X, AlertTriangle, Mail, ShieldAlert, Check, ChevronRight, ExternalLink } from 'lucide-react';
import EmailSugestao from '@/components/EmailSugestao';
import { NaoPerturbeInline } from '@/components/NaoPerturbe';
import { CreditoInline } from '@/components/credito/CreditoInline';
import { ViabilidadeInline } from '@/components/viabilidade/ViabilidadeInline';
import { resumoViabilidade } from '@/components/viabilidade/servicos';
import { patchPlanoClaro, LABEL_KEY } from '@/components/viabilidade/planoVenda';

const NovaVenda = () => {
  const { user } = useAuth();
  const { triggerPendingBlock, pendingBlock } = useJornada();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const [forcarEnvio, setForcarEnvio] = useState(false);
  const [creditoCliente, setCreditoCliente] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [planosDisponiveis, setPlanosDisponiveis] = useState([]);
  
  // Dados vindos do Mailing
  const dadosMailing = location.state?.dadosMailing;
  
  // Estado para correlação de dados (TT)
  const [correlacoes, setCorrelacoes] = useState([]);
  const [verificandoCorrelacao, setVerificandoCorrelacao] = useState(false);
  const [consultandoCnpj, setConsultandoCnpj] = useState(false);

  // Consulta CNPJ via CNPJá (gratuita) + fallback BrasilAPI
  const consultarCNPJ = async (cnpj) => {
    const numeros = cnpj.replace(/\D/g, '');
    if (numeros.length !== 14) return;
    setConsultandoCnpj(true);
    try {
      let razaoSocial = '';
      let inscricaoEstadual = '';
      
      // Tentar CNPJá primeiro (dados mais completos)
      try {
        const cnpjaRes = await fetch(`https://open.cnpja.com/office/${numeros}`);
        if (cnpjaRes.ok) {
          const d = await cnpjaRes.json();
          razaoSocial = (d.alias || d.company?.name || '').toUpperCase();
          if (d.registrations && d.registrations.length > 0) {
            inscricaoEstadual = d.registrations[0]?.number || '';
          }
        }
      } catch (e) {
        console.log('CNPJa indisponivel, usando BrasilAPI');
      }
      
      // Fallback BrasilAPI
      if (!razaoSocial) {
        const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${numeros}`);
        if (response.ok) {
          const data = await response.json();
          razaoSocial = (data.razao_social || '').toUpperCase();
        }
      }
      
      const updates = {};
      if (razaoSocial) updates.nome_razao_social = razaoSocial;
      if (inscricaoEstadual) updates.inscricao_estadual = inscricaoEstadual;
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({
          ...prev,
          cliente: { ...prev.cliente, ...updates }
        }));
        toast.success(inscricaoEstadual 
          ? 'Dados e inscricao estadual preenchidos' 
          : 'Dados preenchidos automaticamente');
      }
    } catch (error) {
      console.error('Erro ao consultar CNPJ:', error);
    } finally {
      setConsultandoCnpj(false);
    }
  };

  // Função para converter texto para maiúsculo
  const toUpperCase = (value) => {
    return typeof value === 'string' ? value.toUpperCase() : value;
  };

  // Função para formatar CPF: 123.456.789-00
  const formatarCPF = (cpf) => {
    const numeros = cpf.replace(/\D/g, '');
    if (numeros.length !== 11) return numeros;
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Função para formatar CNPJ: 12.345.678/0001-99
  const formatarCNPJ = (cnpj) => {
    const numeros = cnpj.replace(/\D/g, '');
    if (numeros.length !== 14) return numeros;
    return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  // Função para formatar documento (CPF ou CNPJ)
  const formatarDocumento = (documento, tipo) => {
    const numeros = documento.replace(/\D/g, '');
    if (tipo === 'CPF') {
      return formatarCPF(numeros);
    } else {
      return formatarCNPJ(numeros);
    }
  };

  // Validar quantidade de dígitos
  const validarDocumento = (documento, tipo) => {
    const numeros = documento.replace(/\D/g, '');
    if (tipo === 'CPF') {
      return numeros.length === 11;
    } else {
      return numeros.length === 14;
    }
  };
  
  // Corrigir telefone celular: adiciona o 9 se estiver faltando
  // DDD(2) + 9 + 8 digitos = 11 digitos
  const corrigirTelefone = (valor) => {
    const nums = valor.replace(/\D/g, '');
    if (nums.length === 10) {
      // Faltando o 9: DDD(2) + 8 digitos -> insere 9 apos DDD
      return nums.slice(0, 2) + '9' + nums.slice(2);
    }
    return nums;
  };

  // Formatar documento enquanto digita
  const handleDocumentoInput = (rawValue) => {
    const numeros = rawValue.replace(/\D/g, '');
    const tipo = formData.cliente.tipo_documento;
    let formatted = numeros;
    
    if (tipo === 'CPF') {
      if (numeros.length > 3) formatted = numeros.slice(0,3) + '.' + numeros.slice(3);
      if (numeros.length > 6) formatted = numeros.slice(0,3) + '.' + numeros.slice(3,6) + '.' + numeros.slice(6);
      if (numeros.length > 9) formatted = numeros.slice(0,3) + '.' + numeros.slice(3,6) + '.' + numeros.slice(6,9) + '-' + numeros.slice(9,11);
    } else {
      if (numeros.length > 2) formatted = numeros.slice(0,2) + '.' + numeros.slice(2);
      if (numeros.length > 5) formatted = numeros.slice(0,2) + '.' + numeros.slice(2,5) + '.' + numeros.slice(5);
      if (numeros.length > 8) formatted = numeros.slice(0,2) + '.' + numeros.slice(2,5) + '.' + numeros.slice(5,8) + '/' + numeros.slice(8);
      if (numeros.length > 12) formatted = numeros.slice(0,2) + '.' + numeros.slice(2,5) + '.' + numeros.slice(5,8) + '/' + numeros.slice(8,12) + '-' + numeros.slice(12,14);
    }
    
    setFormData({
      ...formData,
      cliente: {...formData.cliente, documento: formatted}
    });
  };

  // Form state
  const [formData, setFormData] = useState({
    telefone_venda: '',
    data_prevista_vendedor: '',
    turno_previsto_vendedor: '',
    modalidade_venda: '', // TT ou PROPOSTA ou PROSPECT ou BASE
    // Campos TT - Titular Antigo
    numero_contrato_cancelamento: '',
    nome_titular_antigo: '',
    telefone_titular_antigo: '',
    email_titular_antigo: '',
    cpf_cnpj_titular_antigo: '',
    // Campos BASE
    contrato_cliente_base: '',
    cliente: {
      tipo_documento: 'CPF',
      documento: '',
      nome_razao_social: '',
      email: '',
      telefone1: '',
      telefone2: '',
      nome_mae: '',
      rg: '',
      data_nascimento: ''
    },
    endereco: {
      cep: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      hp: ''
    },
    produtos: [],
    produtos_selecionados: [],
    planos_adicionais: [],
    tipo_venda: 'Base',
    categoria: 'Residencial',
    valor_plano: '',
    plano_tv_id: '',
    plano_internet_id: '',
    plano_fixo_id: '',
    plano_movel_id: '',
    plano_alacarte_id: '',
    plano_pontosextra_id: '',
    plano_pontosadicionais_id: '',
    plano_tv_valor: 0,
    plano_internet_valor: 0,
    plano_fixo_valor: 0,
    plano_movel_valor: 0,
    plano_alacarte_valor: 0,
    plano_pontosextra_valor: 0,
    plano_pontosadicionais_valor: 0,
    plano_tv_nome: '',
    plano_internet_nome: '',
    plano_fixo_nome: '',
    plano_movel_nome: '',
    plano_alacarte_nome: '',
    plano_pontosextra_nome: '',
    plano_pontosadicionais_nome: '',
    plano_tv_qtd: 1,
    plano_internet_qtd: 1,
    plano_fixo_qtd: 1,
    fixo_tipo_operacao: '',
    fixo_numero_portabilidade: '',
    fixo_operadora_portabilidade: '',
    plano_movel_qtd: 1,
    movel_tipo_operacao: '',
    movel_numero_portabilidade: '',
    movel_operadora_portabilidade: '',
    movel_tipo_migracao: '',
    movel_numeros: [],
    movel_possui_dependentes: null,
    movel_dependentes: [],
    movel_numero_migracao: '',
    movel_cpf_cliente: '',
    tt_linhas: [''],
    plano_alacarte_qtd: 1,
    plano_pontosextra_qtd: 1,
    plano_pontosadicionais_qtd: 1,
    tv_possui_ponto_adicional: false,
    internets_adicionais: [],
    viabilidade: null,
    empresa_id: '',
    pagamento: {
      forma_pagamento: 'Débito',
      vencimento_fatura: '10',
      banco: '',
      agencia: '',
      conta: ''
    },
    observacoes_campo: ''
  });

  // Calcular progresso do formulario
  const temPlanoSelecionado = ['tv', 'internet', 'fixo', 'movel', 'alacarte', 'pontosextra', 'pontosadicionais']
    .some(k => formData[`plano_${k}_id`] && formData[`plano_${k}_id`] !== 'nenhum');

  const calcularProgresso = () => {
    const campos = [
      formData.telefone_venda,
      formData.modalidade_venda,
      formData.cliente.documento,
      formData.cliente.nome_razao_social,
      formData.cliente.email,
      formData.cliente.telefone1,
      formData.endereco.cep,
      formData.endereco.endereco,
      formData.endereco.numero,
      formData.endereco.cidade,
      formData.endereco.estado,
      formData.empresa_id,
      formData.pagamento.forma_pagamento,
      formData.pagamento.vencimento_fatura,
      temPlanoSelecionado ? 'ok' : '',
    ];
    const preenchidos = campos.filter(c => c && c.trim && c.trim() !== '' || c).length;
    return Math.round((preenchidos / campos.length) * 100);
  };

  const camposFaltando = () => {
    const missing = [];
    if (!formData.telefone_venda) missing.push('Telefone da Venda');
    if (!formData.modalidade_venda) missing.push('Tipo de Venda');
    if (!formData.cliente.documento) missing.push('CPF/CNPJ');
    if (!formData.cliente.nome_razao_social) missing.push('Nome Completo');
    if (!formData.cliente.email) missing.push('E-mail');
    if (!formData.cliente.telefone1) missing.push('Telefone 1');
    if (!formData.endereco.cep) missing.push('CEP');
    if (!formData.endereco.endereco) missing.push('Endereco');
    if (!formData.endereco.numero) missing.push('Numero');
    if (!formData.endereco.cidade) missing.push('Cidade');
    if (!formData.endereco.estado) missing.push('Estado');
    if (!formData.empresa_id) missing.push('Empresa');
    if (!temPlanoSelecionado) missing.push('Planos e Valores (min. 1 servico)');
    return missing;
  };

  const progresso = calcularProgresso();
  const faltam = camposFaltando();

  const verificarCorrelacao = async (nome, telefone, email) => {
    if (!nome && !telefone && !email) {
      setCorrelacoes([]);
      return;
    }
    setVerificandoCorrelacao(true);
    try {
      const response = await vendasAPI.verificarCorrelacao({
        nome: nome || '',
        telefone: telefone || '',
        email: email || ''
      });
      if (response.data.tem_correlacao) {
        setCorrelacoes(response.data.correlacoes);
        toast.warning(`Atencao: ${response.data.total} registro(s) similar(es) encontrado(s)!`);
      } else {
        setCorrelacoes([]);
      }
    } catch (error) {
      console.error('Erro ao verificar correlacao:', error);
    } finally {
      setVerificandoCorrelacao(false);
    }
  };

  // Consultar CPF automaticamente quando tem 11 digitos
  const [consultandoCpf, setConsultandoCpf] = useState(false);
  const consultarCpf = async (documento) => {
    const nums = documento.replace(/\D/g, '');
    if (nums.length !== 11 || formData.cliente.tipo_documento !== 'CPF') return;
    
    setConsultandoCpf(true);
    try {
      const response = await api.get(`/consulta-cpf/${nums}`);
      const dados = response.data;
      if (dados.nome || dados.data_nascimento || dados.nome_mae) {
        setFormData(prev => ({
          ...prev,
          cliente: {
            ...prev.cliente,
            nome_razao_social: dados.nome ? dados.nome.toUpperCase() : prev.cliente.nome_razao_social,
            data_nascimento: dados.data_nascimento || prev.cliente.data_nascimento,
            nome_mae: dados.nome_mae ? dados.nome_mae.toUpperCase() : prev.cliente.nome_mae,
          }
        }));
        toast.success('Dados do CPF preenchidos automaticamente!');
      }
    } catch (error) {
      console.log('Consulta CPF indisponivel');
    } finally {
      setConsultandoCpf(false);
    }
  };

  useEffect(() => {
    loadEmpresas();
    loadProdutos();
    loadPlanos();
    
    // Se veio dados do Mailing, preencher campos
    if (dadosMailing) {
      toast.success('Dados do Mailing carregados!');
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          cidade: dadosMailing.cidade || '',
          estado: dadosMailing.estado || ''
        },
        observacoes_campo: dadosMailing.observacao 
          ? `[Mailing: ${dadosMailing.numero_proposta}] ${dadosMailing.observacao}`
          : `[Mailing: ${dadosMailing.numero_proposta}]`
      }));
    }
  }, []);

  const loadEmpresas = async () => {
    try {
      const response = await empresasAPI.list();
      setEmpresas(response.data.filter(e => e.ativo));
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };

  const loadProdutos = async () => {
    try {
      const response = await produtosAPI.list();
      setProdutos(response.data.filter(p => p.ativo));
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const loadPlanos = async () => {
    try {
      const response = await valoresAPI.listar({ apenas_ativos: true });
      setPlanosDisponiveis(response.data);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  };

  const handlePlanoChange = (key, planoId) => {
    if (planoId === 'nenhum') {
      const updates = {
        [`plano_${key}_id`]: '',
        [`plano_${key}_valor`]: 0,
        [`plano_${key}_nome`]: '',
      };
      if (key === 'movel') {
        updates.movel_tipo_operacao = '';
        updates.movel_numero_portabilidade = '';
        updates.movel_operadora_portabilidade = '';
        updates.movel_tipo_migracao = '';
        updates.movel_numeros = [];
        updates.movel_possui_dependentes = null;
        updates.movel_dependentes = [];
        updates.movel_numero_migracao = '';
        updates.movel_cpf_cliente = '';
      }
      if (key === 'tv') {
        updates.tv_possui_ponto_adicional = false;
        updates.plano_pontosadicionais_id = ''; updates.plano_pontosadicionais_nome = ''; updates.plano_pontosadicionais_valor = 0; updates.plano_pontosadicionais_qtd = 1;
      }
      if (key === 'internet') {
        updates.internets_adicionais = [];
      }
      if (key === 'fixo') {
        updates.fixo_tipo_operacao = '';
        updates.fixo_numero_portabilidade = '';
        updates.fixo_operadora_portabilidade = '';
      }
      setFormData(prev => ({ ...prev, ...updates }));
    } else {
      const plano = planosDisponiveis.find(p => p.id === planoId);
      if (plano) {
        setFormData(prev => ({
          ...prev,
          [`plano_${key}_id`]: plano.id,
          [`plano_${key}_valor`]: valorPorPagamento(plano, prev.pagamento?.forma_pagamento),
          [`plano_${key}_nome`]: plano.nome_plano,
        }));
      }
    }
  };

  const trocarFormaPagamento = (forma) => {
    const { patch, alterados } = aplicarFormaPagamento(formData, forma, planosDisponiveis);
    setFormData(prev => ({ ...prev, ...patch }));
    if (alterados > 0) toast.info(`Valores dos planos ajustados para ${forma === 'Boleto' ? 'boleto' : 'débito automático'}`);
  };

  const aplicarPlanoClaro = (produto, preco, tipoCodigo) => {
    const r = patchPlanoClaro({ produto, preco, tipoCodigo, planosDisponiveis, categoria: formData.categoria });
    if (!r) { toast.error('Este tipo de produto não tem campo correspondente na venda'); return; }
    setFormData((prev) => ({ ...prev, ...r.patch }));
    toast.success(`${LABEL_KEY[r.key]}: ${r.patch[`plano_${r.key}_nome`]} — R$ ${Number(r.patch[`plano_${r.key}_valor`]).toFixed(2)}${r.match ? '' : ' (plano da Claro, fora da tabela de valores)'}`);
  };

  const valorTotal = 
    (formData.plano_tv_valor || 0) * (formData.plano_tv_qtd || 1) + 
    (formData.plano_internet_valor || 0) * (formData.plano_internet_qtd || 1) + 
    (formData.plano_fixo_valor || 0) * (formData.plano_fixo_qtd || 1) + 
    (formData.plano_movel_valor || 0) * (formData.plano_movel_qtd || 1) + 
    (formData.plano_alacarte_valor || 0) * (formData.plano_alacarte_qtd || 1) + 
    (formData.plano_pontosextra_valor || 0) * (formData.plano_pontosextra_qtd || 1) + 
    (formData.plano_pontosadicionais_valor || 0) * (formData.plano_pontosadicionais_qtd || 1) +
    totalInternetsAdicionais(formData);

  const handleCEPChange = async (cep) => {
    setFormData(prev => ({
      ...prev,
      endereco: { ...prev.endereco, cep }
    }));

    if (cep.replace(/\D/g, '').length === 8) {
      try {
        const response = await cepAPI.buscar(cep);
        if (!response.data.erro) {
          setFormData(prev => ({
            ...prev,
            endereco: {
              ...prev.endereco,
              endereco: response.data.logradouro,
              bairro: (response.data.bairro || '').toUpperCase(),
              cidade: (response.data.localidade || '').toUpperCase(),
              estado: response.data.uf
            }
          }));
          toast.success('CEP encontrado!');
        }
      } catch (error) {
        toast.error('CEP não encontrado');
      }
    }
  };

  const handleProdutoChange = (categoria, produtoId) => {
    setFormData(prev => {
      // Remove qualquer produto anterior desta categoria
      const produtosSemCategoria = prev.produtos.filter(id => {
        const prod = produtos.find(p => p.id === id);
        return prod && prod.categoria !== categoria;
      });
      
      // Adiciona o novo produto se não for vazio
      if (produtoId && produtoId !== '') {
        return { ...prev, produtos: [...produtosSemCategoria, produtoId] };
      }
      
      return { ...prev, produtos: produtosSemCategoria };
    });
  };

  const enviarVenda = async () => {
    setLoading(true);
    try {
      const dadosEnviar = { ...formData, valor_plano: valorTotal || 0 };
      await vendasAPI.create(dadosEnviar);
      toast.success('Venda cadastrada com sucesso!');
      if (pendingBlock && triggerPendingBlock) { triggerPendingBlock(); }
      setFormData({
        telefone_venda: '', data_prevista_vendedor: '', turno_previsto_vendedor: '', modalidade_venda: 'Proposta', contrato_cliente_base: '',
        numero_contrato_cancelamento: '', nome_titular_antigo: '', telefone_titular_antigo: '', email_titular_antigo: '',
        cliente: { tipo_documento: 'CPF', documento: '', nome_razao_social: '', email: '', telefone1: '', telefone2: '', nome_mae: '', rg: '', data_nascimento: '' },
        endereco: { cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', hp: '' },
        viabilidade: null,
        produtos: [], tipo_venda: 'Base', categoria: 'Residencial', valor_plano: '', empresa_id: '',
        pagamento: { forma_pagamento: 'Débito', vencimento_fatura: '10', banco: '', agencia: '', conta: '' },
        observacoes_campo: ''
      });
      setForcarEnvio(false);
    } catch (error) {
      // Tratar erros 422 (validação Pydantic) com mensagens amigáveis
      toast.error(formatarErroAPI(error), { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações básicas
    if (!formData.telefone_venda) { toast.error('Telefone da venda é obrigatório'); return; }

    // Corrigir telefones automaticamente antes de enviar
    const fd = {
      ...formData,
      telefone_venda: corrigirTelefone(formData.telefone_venda),
      telefone_titular_antigo: formData.telefone_titular_antigo ? corrigirTelefone(formData.telefone_titular_antigo) : '',
      cliente: {
        ...formData.cliente,
        telefone1: formData.cliente.telefone1 ? corrigirTelefone(formData.cliente.telefone1) : '',
        telefone2: formData.cliente.telefone2 ? corrigirTelefone(formData.cliente.telefone2) : '',
      }
    };
    setFormData(fd);

    if (!formData.modalidade_venda) { toast.error('Tipo de Venda é obrigatório'); return; }
    if (formData.modalidade_venda === 'TT') {
      if (!formData.numero_contrato_cancelamento) { toast.error('Número do Contrato Antigo é obrigatório para TT'); return; }
      if (!formData.nome_titular_antigo) { toast.error('Nome do Titular Antigo é obrigatório para TT'); return; }
      if (!formData.telefone_titular_antigo) { toast.error('Telefone do Titular Antigo é obrigatório para TT'); return; }
      if (!formData.cpf_cnpj_titular_antigo) { toast.error('CPF/CNPJ do Cliente Anterior é obrigatório para TT'); return; }
    }
    if (!formData.cliente.documento) { toast.error('Documento é obrigatório'); return; }
    if (!validarDocumento(formData.cliente.documento, formData.cliente.tipo_documento)) {
      toast.error(formData.cliente.tipo_documento === 'CPF' ? 'CPF inválido - deve ter 11 dígitos' : 'CNPJ inválido - deve ter 14 dígitos');
      return;
    }
    if (!formData.cliente.nome_razao_social) { toast.error('Nome/Razão Social é obrigatório'); return; }
    if (!formData.empresa_id) { toast.error('Selecione uma empresa'); return; }

    // Validar que pelo menos um plano/serviço foi selecionado
    const temPlano = ['tv', 'internet', 'fixo', 'movel', 'alacarte', 'pontosextra', 'pontosadicionais']
      .some(key => formData[`plano_${key}_id`] && formData[`plano_${key}_id`] !== 'nenhum');
    if (!temPlano) {
      toast.error('Selecione pelo menos um serviço em Planos e Valores');
      return;
    }
    if (formData.plano_movel_id && formData.plano_movel_id !== 'nenhum') {
      const erroMovel = validarMovel(formData);
      if (erroMovel) { toast.error(erroMovel); return; }
    }
    const erroExtras = validarPlanosExtras(formData, temTiposPontoAdicional(planosDisponiveis, formData.categoria));
    if (erroExtras) { toast.error(erroExtras); return; }

    // Se já confirmou, enviar direto
    if (forcarEnvio) {
      await enviarVenda();
      return;
    }

    // Verificar duplicidade (apenas aviso, não bloqueia)
    setLoading(true);
    try {
      const checkRes = await api.post('/vendas/verificar-duplicidade', {
        documento: formData.cliente.documento,
        nome: formData.cliente.nome_razao_social,
        telefone1: formData.cliente.telefone1,
        telefone2: formData.cliente.telefone2,
        modalidade_venda: formData.modalidade_venda
      });
      const dup = checkRes.data;

      if (dup.bloqueado || dup.alerta) {
        // Mostrar aviso mas permitir envio - BKO/Admin vai liberar
        setDupInfo(dup);
        setDupDialogOpen(true);
        setLoading(false);
        return;
      }
      // Sem duplicidade
      await enviarVenda();
    } catch (error) {
      await enviarVenda();
    }
  };

  return (
    <div className="max-w-7xl mx-auto pb-8" data-testid="nova-venda-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
          <span>CRM</span>
          <ChevronRight className="h-3 w-3" />
          <span>Vendas</span>
          <ChevronRight className="h-3 w-3" />
          <span className="text-red-600 font-medium">Nova</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Nova Venda</h1>
        <p className="text-sm text-gray-500 mt-1">Cadastre uma nova venda no sistema</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ===== LEFT COLUMN: FORM ===== */}
          <div className="lg:col-span-2 space-y-5">

            {/* INFORMACOES INICIAIS */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-info-iniciais">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-red-600 rounded-full" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Informacoes Iniciais</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Telefone da Venda *</Label>
                  <div className="relative">
                    <Input
                      id="telefone_venda"
                      data-testid="telefone-venda-input"
                      value={formData.telefone_venda}
                      onChange={(e) => setFormData({...formData, telefone_venda: e.target.value})}
                      onBlur={(e) => {
                        if (e.target.value) setFormData(prev => ({...prev, telefone_venda: corrigirTelefone(e.target.value)}));
                      }}
                      placeholder="(00) 00000-0000"
                      className="pr-8"
                    />
                    {formData.telefone_venda && (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <NaoPerturbeInline telefone={formData.telefone_venda} testid="lnp-inline-telefone-venda" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Tipo de Venda *</Label>
                  <Select
                    value={formData.modalidade_venda}
                    onValueChange={(value) => setFormData({...formData, modalidade_venda: value, tipo_venda: value})}
                  >
                    <SelectTrigger data-testid="modalidade-venda-select" className={!formData.modalidade_venda ? 'border-red-300' : ''}>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Base">Base</SelectItem>
                      <SelectItem value="TT">TT</SelectItem>
                      <SelectItem value="Proposta">Proposta</SelectItem>
                    </SelectContent>
                  </Select>
                  {!formData.modalidade_venda && (
                    <p className="text-red-500 text-[10px] mt-1">* Campo obrigatorio</p>
                  )}
                </div>
              </div>
            </div>

            {/* TT - Dados do Titular Antigo */}
            {formData.modalidade_venda === 'TT' && (
              <div className="bg-orange-50 rounded-xl border border-orange-200 p-5" data-testid="section-tt">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-5 bg-orange-500 rounded-full" />
                  <h2 className="text-xs font-bold text-orange-700 uppercase tracking-wider">Dados do Titular Antigo (Cancelamento)</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1.5 block">Numero do Contrato Antigo *</Label>
                    <Input
                      type="text"
                      data-testid="contrato-antigo-input"
                      value={formData.numero_contrato_cancelamento}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setFormData({...formData, numero_contrato_cancelamento: value});
                      }}
                      placeholder="Digite o numero do contrato"
                    />
                    {!formData.numero_contrato_cancelamento && (
                      <p className="text-red-500 text-[10px] mt-1">* Obrigatorio</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1.5 block">Nome do Titular Antigo *</Label>
                    <Input
                      data-testid="nome-titular-antigo-input"
                      value={formData.nome_titular_antigo}
                      onChange={(e) => setFormData({...formData, nome_titular_antigo: toUpperCase(e.target.value)})}
                      placeholder="Nome completo do titular antigo"
                      className="uppercase"
                    />
                    {!formData.nome_titular_antigo && (
                      <p className="text-red-500 text-[10px] mt-1">* Obrigatorio</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1.5 block">Telefone do Titular Antigo *</Label>
                    <Input
                      data-testid="telefone-titular-antigo-input"
                      value={formData.telefone_titular_antigo}
                      onChange={(e) => setFormData({...formData, telefone_titular_antigo: e.target.value})}
                      onBlur={(e) => {
                        if (e.target.value) setFormData(prev => ({...prev, telefone_titular_antigo: corrigirTelefone(e.target.value)}));
                      }}
                      placeholder="(00) 00000-0000"
                    />
                    {!formData.telefone_titular_antigo && (
                      <p className="text-red-500 text-[10px] mt-1">* Obrigatorio</p>
                    )}
                    <NaoPerturbeInline telefone={formData.telefone_titular_antigo} testid="lnp-inline-telefone-titular-antigo" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1.5 block">E-mail do Titular Antigo</Label>
                    <Input
                      type="email"
                      data-testid="email-titular-antigo-input"
                      value={formData.email_titular_antigo}
                      onChange={(e) => setFormData({...formData, email_titular_antigo: e.target.value.toLowerCase()})}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1.5 block">CPF/CNPJ do Cliente Anterior *</Label>
                    <Input
                      data-testid="cpf-cnpj-titular-antigo-input"
                      value={formData.cpf_cnpj_titular_antigo}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setFormData({...formData, cpf_cnpj_titular_antigo: value});
                      }}
                      placeholder="Somente números"
                      maxLength={14}
                    />
                    {!formData.cpf_cnpj_titular_antigo && (
                      <p className="text-red-500 text-[10px] mt-1">* Obrigatorio</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* BASE - Contrato do Cliente */}
            {['Base', 'BASE'].includes(formData.modalidade_venda) && (
              <div className="bg-red-50 rounded-xl border border-red-200 p-5" data-testid="section-base">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-5 bg-red-600 rounded-full" />
                  <h2 className="text-xs font-bold text-red-800 uppercase tracking-wider">Dados da Base - Contrato do Cliente</h2>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Contrato do Cliente *</Label>
                  <Input
                    value={formData.contrato_cliente_base}
                    onChange={(e) => setFormData({...formData, contrato_cliente_base: e.target.value.toUpperCase()})}
                    placeholder="Numero do contrato do cliente"
                    className="uppercase"
                    data-testid="contrato-base-input"
                  />
                  {!formData.contrato_cliente_base && (
                    <p className="text-red-500 text-[10px] mt-1">* Obrigatorio</p>
                  )}
                </div>
              </div>
            )}

            {/* Alerta de Correlacao (TT) */}
            {formData.modalidade_venda === 'TT' && correlacoes.length > 0 && (
              <div className="bg-red-50 rounded-xl border border-red-300 p-5" data-testid="section-correlacao">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <h2 className="text-sm font-bold text-red-700 uppercase">Alerta de Correlacao</h2>
                </div>
                <p className="text-red-600 text-sm mb-3">
                  Foram encontrados {correlacoes.length} registro(s) com dados similares:
                </p>
                <div className="space-y-2">
                  {correlacoes.map((c, index) => (
                    <div key={index} className="bg-white border border-red-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-bold text-red-700 text-sm">{c.tipo}:</span>{' '}
                          <span className="text-gray-800 text-sm">{c.valor_encontrado}</span>
                        </div>
                        <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded">
                          {c.protocolo}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        Vendedor: {c.vendedor} | Data: {c.data}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DADOS DO CLIENTE */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-dados-cliente">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-red-600 rounded-full" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Dados do Cliente</h2>
              </div>

              {/* CPF/CNPJ Toggle */}
              <div className="mb-4">
                <div className="inline-flex rounded-lg overflow-hidden border border-gray-200" data-testid="cpf-cnpj-toggle">
                  <button
                    type="button"
                    data-testid="toggle-cpf"
                    className={`px-5 py-2 text-sm font-semibold transition-all ${
                      formData.cliente.tipo_documento === 'CPF'
                        ? 'bg-red-600 text-white shadow-inner'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    onClick={() => setFormData({
                      ...formData,
                      cliente: {...formData.cliente, tipo_documento: 'CPF', documento: ''}
                    })}
                  >
                    CPF
                  </button>
                  <button
                    type="button"
                    data-testid="toggle-cnpj"
                    className={`px-5 py-2 text-sm font-semibold transition-all ${
                      formData.cliente.tipo_documento === 'CNPJ'
                        ? 'bg-red-600 text-white shadow-inner'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    onClick={() => setFormData({
                      ...formData,
                      cliente: {...formData.cliente, tipo_documento: 'CNPJ', documento: ''}
                    })}
                  >
                    CNPJ
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs text-gray-500 block">{formData.cliente.tipo_documento} *</Label>
                    <button
                      type="button"
                      data-testid="consultar-cnpja-btn"
                      title="Abre a Pesquisa de Sócios do CNPJá em nova aba para verificar se o CPF/nome possui CNPJ"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-600 hover:text-purple-800 hover:underline"
                      onClick={() => {
                        const doc = (formData.cliente.documento || '').replace(/\D/g, '');
                        if (doc) {
                          navigator.clipboard?.writeText(doc).then(
                            () => toast.info(`${formData.cliente.tipo_documento} copiado — cole no campo de busca do CNPJá`),
                            () => {}
                          );
                        }
                        window.open('https://cnpja.com/person', '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <ExternalLink className="h-3 w-3" />
                      Tem CNPJ? Consultar
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      data-testid="documento-input"
                      value={formData.cliente.documento}
                      onChange={(e) => {
                        const numeros = e.target.value.replace(/\D/g, '');
                        setFormData({
                          ...formData,
                          cliente: {...formData.cliente, documento: numeros}
                        });
                        // Auto-consulta ao digitar 14 dígitos (CNPJ) ou 11 (CPF)
                        if (formData.cliente.tipo_documento === 'CNPJ' && numeros.length === 14) {
                          consultarCNPJ(numeros);
                        }
                        if (formData.cliente.tipo_documento === 'CPF' && numeros.length === 11) {
                          consultarCpf(numeros);
                        }
                      }}
                      onBlur={(e) => {
                        const numeros = e.target.value.replace(/\D/g, '');
                        const formatado = formatarDocumento(numeros, formData.cliente.tipo_documento);
                        setFormData({
                          ...formData,
                          cliente: {...formData.cliente, documento: formatado}
                        });
                        if (formData.cliente.tipo_documento === 'CNPJ' && numeros.length === 14) {
                          consultarCNPJ(numeros);
                        }
                        if (formData.cliente.tipo_documento === 'CPF' && numeros.length === 11) {
                          consultarCpf(numeros);
                        }
                      }}
                      placeholder={formData.cliente.tipo_documento === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                      className="pr-8"
                    />
                    {formData.cliente.documento && validarDocumento(formData.cliente.documento, formData.cliente.tipo_documento) && (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    )}
                  </div>
                  {formData.cliente.documento && !validarDocumento(formData.cliente.documento, formData.cliente.tipo_documento) && (
                    <p className="text-red-500 text-[10px] mt-1">
                      {formData.cliente.tipo_documento === 'CPF' ? 'CPF deve ter 11 digitos' : 'CNPJ deve ter 14 digitos'}
                    </p>
                  )}
                  <CreditoInline
                    documento={formData.cliente.documento}
                    tipoDocumento={formData.cliente.tipo_documento}
                    nome={formData.cliente.nome_razao_social}
                    dataNascimento={formData.cliente.data_nascimento}
                    cep={formData.endereco.cep}
                    cidade={formData.endereco.cidade}
                    uf={formData.endereco.estado}
                    categoria={formData.categoria}
                    modalidade={formData.modalidade_venda}
                    contrato={formData.contrato_cliente_base}
                    onResultado={setCreditoCliente}
                    testid="credito-inline-documento"
                  />
                </div>

                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Nome / Razao Social *</Label>
                  <div className="relative">
                    <Input
                      data-testid="nome-razao-input"
                      value={formData.cliente.nome_razao_social}
                      onChange={(e) => setFormData({
                        ...formData,
                        cliente: {...formData.cliente, nome_razao_social: toUpperCase(e.target.value)}
                      })}
                      onBlur={(e) => {
                        if (formData.modalidade_venda === 'TT' && e.target.value) {
                          verificarCorrelacao(e.target.value, formData.cliente.telefone1, formData.cliente.email);
                        }
                      }}
                      className={`uppercase pr-8 ${(consultandoCnpj || consultandoCpf) ? 'animate-pulse bg-gray-50' : ''}`}
                      placeholder={(consultandoCnpj || consultandoCpf) ? 'Consultando dados...' : ''}
                      disabled={consultandoCnpj || consultandoCpf}
                    />
                    {(consultandoCnpj || consultandoCpf) ? (
                      <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500 animate-spin" />
                    ) : formData.cliente.nome_razao_social ? (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    ) : null}
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">E-mail</Label>
                  <div className="relative">
                    <EmailSugestao
                      data-testid="email-input"
                      value={formData.cliente.email}
                      onChange={(val) => setFormData({
                        ...formData,
                        cliente: {...formData.cliente, email: val}
                      })}
                      onBlur={(e) => {
                        if (formData.modalidade_venda === 'TT' && e.target.value) {
                          verificarCorrelacao(formData.cliente.nome_razao_social, formData.cliente.telefone1, e.target.value);
                        }
                      }}
                      className="pr-8"
                    />
                    {formData.cliente.email && formData.cliente.email.includes('@') && (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500 pointer-events-none" />
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Telefone 1 *</Label>
                  <div className="relative">
                    <Input
                      data-testid="telefone1-input"
                      value={formData.cliente.telefone1}
                      onChange={(e) => setFormData({
                        ...formData,
                        cliente: {...formData.cliente, telefone1: e.target.value}
                      })}
                      onBlur={(e) => {
                        const corrigido = e.target.value ? corrigirTelefone(e.target.value) : '';
                        if (corrigido !== e.target.value) {
                          setFormData(prev => ({...prev, cliente: {...prev.cliente, telefone1: corrigido}}));
                        }
                        if (formData.modalidade_venda === 'TT' && e.target.value) {
                          verificarCorrelacao(formData.cliente.nome_razao_social, corrigido || e.target.value, formData.cliente.email);
                        }
                      }}
                      className="pr-8"
                    />
                    {formData.cliente.telefone1 && (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <NaoPerturbeInline telefone={formData.cliente.telefone1} testid="lnp-inline-telefone1" />
                </div>

                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Telefone 2</Label>
                  <Input
                    data-testid="telefone2-input"
                    value={formData.cliente.telefone2}
                    onChange={(e) => setFormData({
                      ...formData,
                      cliente: {...formData.cliente, telefone2: e.target.value}
                    })}
                    onBlur={(e) => {
                      if (e.target.value) setFormData(prev => ({...prev, cliente: {...prev.cliente, telefone2: corrigirTelefone(e.target.value)}}));
                    }}
                  />
                  <NaoPerturbeInline telefone={formData.cliente.telefone2} testid="lnp-inline-telefone2" />
                </div>

                {/* Campos CPF */}
                {formData.cliente.tipo_documento === 'CPF' && (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Nome da Mae *</Label>
                      <Input
                        data-testid="nome-mae-input"
                        value={formData.cliente.nome_mae}
                        onChange={(e) => setFormData({
                          ...formData,
                          cliente: {...formData.cliente, nome_mae: toUpperCase(e.target.value)}
                        })}
                        className="uppercase"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">RG *</Label>
                      <Input
                        data-testid="rg-input"
                        value={formData.cliente.rg}
                        onChange={(e) => setFormData({
                          ...formData,
                          cliente: {...formData.cliente, rg: toUpperCase(e.target.value)}
                        })}
                        className="uppercase"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Data de Nascimento *</Label>
                      <Input
                        type="date"
                        data-testid="data-nascimento-input"
                        value={formData.cliente.data_nascimento}
                        onChange={(e) => setFormData({
                          ...formData,
                          cliente: {...formData.cliente, data_nascimento: e.target.value}
                        })}
                      />
                    </div>
                  </>
                )}

                {/* Campos CNPJ - Empresarial */}
                {formData.cliente.tipo_documento === 'CNPJ' && (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Representante Legal</Label>
                      <Input
                        data-testid="representante-legal-input"
                        value={formData.cliente.representante_legal || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          cliente: {...formData.cliente, representante_legal: toUpperCase(e.target.value)}
                        })}
                        placeholder="Nome do representante legal"
                        className="uppercase"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Inscricao Estadual</Label>
                      <Input
                        data-testid="inscricao-estadual-input"
                        value={formData.cliente.inscricao_estadual || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          cliente: {...formData.cliente, inscricao_estadual: e.target.value.replace(/\D/g, '')}
                        })}
                        placeholder="Numero da inscricao estadual"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ENDERECO */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-endereco">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-red-600 rounded-full" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Endereco</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">CEP *</Label>
                  <Input
                    data-testid="cep-input"
                    value={formData.endereco.cep}
                    onChange={(e) => handleCEPChange(e.target.value)}
                    placeholder="00000-000"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-gray-500 mb-1.5 block">Endereco *</Label>
                  <div className="relative">
                    <Input
                      data-testid="endereco-input"
                      value={formData.endereco.endereco}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: {...formData.endereco, endereco: toUpperCase(e.target.value)}
                      })}
                      className="uppercase pr-8"
                    />
                    {formData.endereco.endereco && (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Numero *</Label>
                  <Input
                    data-testid="numero-input"
                    value={formData.endereco.numero}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: {...formData.endereco, numero: toUpperCase(e.target.value)}
                    })}
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Complemento</Label>
                  <Input
                    data-testid="complemento-input"
                    value={formData.endereco.complemento}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: {...formData.endereco, complemento: toUpperCase(e.target.value)}
                    })}
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Bairro</Label>
                  <Input
                    data-testid="bairro-input"
                    value={formData.endereco.bairro}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: {...formData.endereco, bairro: toUpperCase(e.target.value)}
                    })}
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Cidade *</Label>
                  <div className="relative">
                    <Input
                      data-testid="cidade-input"
                      value={formData.endereco.cidade}
                      onChange={(e) => setFormData({
                        ...formData,
                        endereco: {...formData.endereco, cidade: toUpperCase(e.target.value)}
                      })}
                      className="uppercase pr-8"
                    />
                    {formData.endereco.cidade && (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Estado *</Label>
                  <Input
                    data-testid="estado-input"
                    value={formData.endereco.estado}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: {...formData.endereco, estado: toUpperCase(e.target.value)}
                    })}
                    maxLength={2}
                    placeholder="ES"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">HP</Label>
                  <Input
                    data-testid="hp-input"
                    value={formData.endereco.hp}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: {...formData.endereco, hp: toUpperCase(e.target.value)}
                    })}
                    placeholder="HP do endereco"
                    className="uppercase"
                  />
                </div>
              </div>
              <div className="mt-4">
                <ViabilidadeInline
                  cep={formData.endereco.cep}
                  numero={formData.endereco.numero}
                  cidade={formData.endereco.cidade}
                  uf={formData.endereco.estado}
                  complemento={formData.endereco.complemento}
                  tipoCliente={formData.categoria === 'Empresarial' ? 'EMPRESARIAL' : 'RESIDENCIAL'}
                  credito={creditoCliente}
                  onEscolherPlano={aplicarPlanoClaro}
                  planos={{ tv: !!formData.plano_tv_id, internet: !!formData.plano_internet_id || (formData.internets_adicionais || []).length > 0, fixo: !!formData.plano_fixo_id }}
                  onResultado={(d) => setFormData((prev) => ({
                    ...prev,
                    viabilidade: d ? resumoViabilidade(d, user?.nome) : null,
                    endereco: { ...prev.endereco, hp: d ? String(d.hp) : (prev.viabilidade?.hp && String(prev.viabilidade.hp) === prev.endereco.hp ? '' : prev.endereco.hp) },
                  }))}
                  onPreencherEndereco={(d) => setFormData((prev) => ({
                    ...prev,
                    endereco: {
                      ...prev.endereco,
                      cep: d.cep ? d.cep.replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2') : prev.endereco.cep,
                      endereco: toUpperCase(d.logradouro || prev.endereco.endereco),
                      numero: d.numero || prev.endereco.numero,
                      complemento: toUpperCase(d.complementos || ''),
                      bairro: toUpperCase(d.bairro || prev.endereco.bairro),
                      cidade: toUpperCase(d.cidade || prev.endereco.cidade),
                      estado: (d.uf || prev.endereco.estado || '').toUpperCase(),
                      hp: String(d.hp),
                    },
                  }))}
                  testid="viab-inline-endereco"
                />
              </div>
            </div>

            {/* INFORMACOES DA VENDA */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-info-venda">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-red-600 rounded-full" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Informacoes da Venda</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Categoria (PF/PJ) *</Label>
                  <div className="inline-flex rounded-lg overflow-hidden border border-gray-200" data-testid="categoria-toggle">
                    <button
                      type="button"
                      data-testid="toggle-residencial"
                      className={`px-5 py-2 text-sm font-semibold transition-all ${
                        formData.categoria === 'Residencial'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setFormData({...formData, categoria: 'Residencial',
                        plano_tv_id: '', plano_tv_nome: '', plano_tv_valor: 0, plano_tv_qtd: 1,
                        plano_internet_id: '', plano_internet_nome: '', plano_internet_valor: 0, plano_internet_qtd: 1,
                        plano_fixo_id: '', plano_fixo_nome: '', plano_fixo_valor: 0, plano_fixo_qtd: 1,
                        plano_movel_id: '', plano_movel_nome: '', plano_movel_valor: 0, plano_movel_qtd: 1,
                        plano_alacarte_id: '', plano_alacarte_nome: '', plano_alacarte_valor: 0, plano_alacarte_qtd: 1,
                        plano_pontosextra_id: '', plano_pontosextra_nome: '', plano_pontosextra_valor: 0, plano_pontosextra_qtd: 1,
                        plano_pontosadicionais_id: '', plano_pontosadicionais_nome: '', plano_pontosadicionais_valor: 0, plano_pontosadicionais_qtd: 1,
                        tv_possui_ponto_adicional: false, internets_adicionais: [],
                      })}
                    >
                      Residencial (PF)
                    </button>
                    <button
                      type="button"
                      data-testid="toggle-empresarial"
                      className={`px-5 py-2 text-sm font-semibold transition-all ${
                        formData.categoria === 'Empresarial'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setFormData({...formData, categoria: 'Empresarial',
                        plano_tv_id: '', plano_tv_nome: '', plano_tv_valor: 0, plano_tv_qtd: 1,
                        plano_internet_id: '', plano_internet_nome: '', plano_internet_valor: 0, plano_internet_qtd: 1,
                        plano_fixo_id: '', plano_fixo_nome: '', plano_fixo_valor: 0, plano_fixo_qtd: 1,
                        plano_movel_id: '', plano_movel_nome: '', plano_movel_valor: 0, plano_movel_qtd: 1,
                        plano_alacarte_id: '', plano_alacarte_nome: '', plano_alacarte_valor: 0, plano_alacarte_qtd: 1,
                        plano_pontosextra_id: '', plano_pontosextra_nome: '', plano_pontosextra_valor: 0, plano_pontosextra_qtd: 1,
                        plano_pontosadicionais_id: '', plano_pontosadicionais_nome: '', plano_pontosadicionais_valor: 0, plano_pontosadicionais_qtd: 1,
                        tv_possui_ponto_adicional: false, internets_adicionais: [],
                      })}
                    >
                      Empresarial (PJ)
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Empresa *</Label>
                  <Select
                    value={formData.empresa_id}
                    onValueChange={(value) => setFormData({...formData, empresa_id: value})}
                  >
                    <SelectTrigger data-testid="empresa-select">
                      <SelectValue placeholder="Selecione uma empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      {empresas.filter(empresa => empresa.ativo !== false).map((empresa) => (
                        <SelectItem key={empresa.id} value={empresa.id}>
                          {empresa.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* PLANOS E VALORES */}
            <div className={`bg-white rounded-xl border p-5 ${
              !['tv', 'internet', 'fixo', 'movel', 'alacarte', 'pontosextra', 'pontosadicionais'].some(k => formData[`plano_${k}_id`] && formData[`plano_${k}_id`] !== 'nenhum')
                ? 'border-red-300' : 'border-gray-200'
            }`} data-testid="section-planos">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-red-600 rounded-full" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Planos e Valores *</h2>
              </div>
              {!['tv', 'internet', 'fixo', 'movel', 'alacarte', 'pontosextra', 'pontosadicionais'].some(k => formData[`plano_${k}_id`] && formData[`plano_${k}_id`] !== 'nenhum') && (
                <p className="text-red-500 text-xs mb-3 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Selecione pelo menos um servico
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { tipo: 'TV', key: 'tv', label: 'TV' },
                  { tipo: 'INTERNET', key: 'internet', label: 'Internet' },
                  { tipo: 'FIXO', key: 'fixo', label: 'Fixo' },
                  { tipo: 'MOVEL', key: 'movel', label: 'Movel' },
                  { tipo: 'A LA CARTE', key: 'alacarte', label: 'A La Carte' },
                  { tipo: 'PONTOS EXTRA', key: 'pontosextra', label: 'Pontos Extra' },
                ].map(({ tipo, key, label }) => {
                  const planosTipo = planosDisponiveis.filter(p => p.tipo === tipo && p.categoria === formData.categoria);
                  const planoId = formData[`plano_${key}_id`];
                  const planoValor = formData[`plano_${key}_valor`];
                  return (
                    <div key={tipo} className="space-y-1.5">
                      <Label className="text-xs text-gray-500">{label}</Label>
                      <div className="flex gap-2">
                        <Select
                          value={planoId || 'nenhum'}
                          onValueChange={(v) => handlePlanoChange(key, v)}
                        >
                          <SelectTrigger data-testid={`plano-${key}-select`} className="flex-1">
                            <SelectValue placeholder={`Selecione ${label}...`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nenhum">Nenhum</SelectItem>
                            {planoId?.startsWith('claro-') && (
                              <SelectItem value={planoId} data-testid={`plano-${key}-opcao-claro`}>
                                {formData[`plano_${key}_nome`]} (R$ {Number(planoValor || 0).toFixed(2)}) • Claro
                              </SelectItem>
                            )}
                            {planosTipo.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {labelPlano(p, formData.pagamento.forma_pagamento)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {planoId && planoId !== 'nenhum' && (
                          <Input
                            type="number"
                            min={1}
                            value={formData[`plano_${key}_qtd`] || 1}
                            onChange={(e) => setFormData({...formData, [`plano_${key}_qtd`]: Math.max(1, parseInt(e.target.value) || 1)})}
                            className="w-16 text-center"
                            data-testid={`plano-${key}-qtd`}
                          />
                        )}
                      </div>
                      {planoValor > 0 && (
                        <p className="text-[11px] text-red-600 font-medium">
                          R$ {planoValor.toFixed(2)} x {formData[`plano_${key}_qtd`] || 1} = R$ {(planoValor * (formData[`plano_${key}_qtd`] || 1)).toFixed(2)}
                          {(() => { const pt = planosTipo.find(p => p.id === planoId); const vt = pt ? valorPorPagamento(pt, formData.pagamento.forma_pagamento) : null; return pt && Math.abs(vt - planoValor) > 0.009 ? <span className="text-gray-500 font-normal"> • valor da Claro (tabela R$ {vt.toFixed(2)})</span> : null; })()}
                        </p>
                      )}
                      {key === 'tv' && planoId && planoId !== 'nenhum' && (
                        <TvPontosAdicionais data={formData} onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))} planosDisponiveis={planosDisponiveis} categoria={formData.categoria} formaPagamento={formData.pagamento.forma_pagamento} />
                      )}
                      {key === 'internet' && planoId && planoId !== 'nenhum' && (
                        <InternetsAdicionais data={formData} onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))} planosDisponiveis={planosDisponiveis} categoria={formData.categoria} formaPagamento={formData.pagamento.forma_pagamento} />
                      )}
                      {/* Campos condicionais Fixo */}
                      {key === 'fixo' && planoId && planoId !== 'nenhum' && (
                        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3" data-testid="fixo-opcoes">
                          <Label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Tipo de Operação</Label>
                          <div className="flex gap-2 flex-wrap">
                            {['Linha Nova', 'Portabilidade'].map(op => (
                              <button
                                key={op}
                                type="button"
                                onClick={() => setFormData(prev => ({
                                  ...prev,
                                  fixo_tipo_operacao: prev.fixo_tipo_operacao === op ? '' : op,
                                  fixo_numero_portabilidade: op !== 'Portabilidade' ? '' : prev.fixo_numero_portabilidade,
                                  fixo_operadora_portabilidade: op !== 'Portabilidade' ? '' : prev.fixo_operadora_portabilidade,
                                }))}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                  formData.fixo_tipo_operacao === op
                                    ? 'bg-red-600 text-white border-red-600'
                                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-red-400'
                                }`}
                                data-testid={`fixo-op-${op.toLowerCase().replace(/\s/g, '-')}`}
                              >
                                {op}
                              </button>
                            ))}
                          </div>
                          {formData.fixo_tipo_operacao === 'Portabilidade' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs text-gray-500">Número com DDD</Label>
                                <Input
                                  placeholder="(00) 0000-0000"
                                  value={formData.fixo_numero_portabilidade}
                                  onChange={(e) => setFormData(prev => ({ ...prev, fixo_numero_portabilidade: e.target.value }))}
                                  data-testid="fixo-numero-portabilidade"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-gray-500">Operadora</Label>
                                <Input
                                  placeholder="Ex: Vivo, Oi, Claro..."
                                  value={formData.fixo_operadora_portabilidade}
                                  onChange={(e) => setFormData(prev => ({ ...prev, fixo_operadora_portabilidade: e.target.value }))}
                                  data-testid="fixo-operadora-portabilidade"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Campos condicionais Móvel */}
                      {key === 'movel' && planoId && planoId !== 'nenhum' && (
                        <MovelContratacao
                          data={formData}
                          onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                          plano={planosDisponiveis.find(p => p.id === planoId)}
                          tipoVenda={formData.tipo_venda}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 p-4 bg-red-600 rounded-xl flex justify-between items-center" data-testid="valor-total-bar">
                <span className="text-sm font-semibold text-white/80">Valor Total da Venda:</span>
                <span className="text-xl font-bold text-white" data-testid="valor-total-venda">
                  R$ {valorTotal.toFixed(2)}
                </span>
              </div>
            </div>

            {/* INFORMACOES DE PAGAMENTO */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-pagamento">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-red-600 rounded-full" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Informacoes de Pagamento</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-500 mb-1.5 block">Forma de Pagamento *</Label>
                  <div className="inline-flex rounded-lg overflow-hidden border border-gray-200" data-testid="pagamento-toggle">
                    <button
                      type="button"
                      data-testid="toggle-boleto"
                      className={`px-5 py-2 text-sm font-semibold transition-all ${
                        formData.pagamento.forma_pagamento === 'Boleto'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                      onClick={() => trocarFormaPagamento('Boleto')}
                    >
                      Boleto
                    </button>
                    <button
                      type="button"
                      data-testid="toggle-debito"
                      className={`px-5 py-2 text-sm font-semibold transition-all ${
                        formData.pagamento.forma_pagamento === 'Débito'
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                      onClick={() => trocarFormaPagamento('Débito')}
                    >
                      Debito Automatico
                    </button>
                  </div>
                  {formData.categoria === 'Empresarial' && planosDisponiveis.some(p => p.categoria === 'Empresarial' && p.valor_boleto != null) && (
                    <p className="text-[11px] text-gray-500 mt-1" data-testid="pagamento-aviso-valores">Empresarial: os valores dos planos mudam conforme boleto ou débito automático — ajustados automaticamente.</p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1.5 block">Vencimento da Fatura *</Label>
                    <Select
                      value={formData.pagamento.vencimento_fatura}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        pagamento: {...formData.pagamento, vencimento_fatura: value}
                      })}
                    >
                      <SelectTrigger data-testid="vencimento-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="05">Dia 05</SelectItem>
                        <SelectItem value="08">Dia 08</SelectItem>
                        <SelectItem value="10">Dia 10</SelectItem>
                        <SelectItem value="15">Dia 15</SelectItem>
                        <SelectItem value="20">Dia 20</SelectItem>
                        <SelectItem value="25">Dia 25</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.pagamento.forma_pagamento === 'Débito' && (
                    <>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1.5 block">Banco</Label>
                        <Input
                          data-testid="banco-input"
                          value={formData.pagamento.banco}
                          onChange={(e) => setFormData({
                            ...formData,
                            pagamento: {...formData.pagamento, banco: toUpperCase(e.target.value)}
                          })}
                          className="uppercase"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1.5 block">Agencia</Label>
                        <Input
                          data-testid="agencia-input"
                          value={formData.pagamento.agencia}
                          onChange={(e) => setFormData({
                            ...formData,
                            pagamento: {...formData.pagamento, agencia: toUpperCase(e.target.value)}
                          })}
                          className="uppercase"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 mb-1.5 block">Conta</Label>
                        <Input
                          data-testid="conta-input"
                          value={formData.pagamento.conta}
                          onChange={(e) => setFormData({
                            ...formData,
                            pagamento: {...formData.pagamento, conta: toUpperCase(e.target.value)}
                          })}
                          className="uppercase"
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="pt-3 border-t border-dashed border-gray-200">
                  <Label className="text-xs text-gray-500 mb-1.5 block">Data prevista de instalação — 100% (sua previsão)</Label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <Input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={formData.data_prevista_vendedor}
                      onChange={(e) => setFormData({...formData, data_prevista_vendedor: e.target.value})}
                      className="max-w-xs"
                      data-testid="data-prevista-vendedor"
                    />
                    <TurnoToggle
                      value={formData.turno_previsto_vendedor}
                      onChange={(t) => setFormData({...formData, turno_previsto_vendedor: t})}
                      testidPrefix="turno-vendedor"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">Data e turno em que você realmente prevê a instalação. O BKO registrará a previsão oficial no CRM.</p>
                </div>
              </div>
            </div>

            {/* OBSERVACOES */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-observacoes">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-red-600 rounded-full" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Observacoes</h2>
              </div>
              <Textarea
                data-testid="observacoes-input"
                value={formData.observacoes_campo}
                onChange={(e) => setFormData({...formData, observacoes_campo: e.target.value})}
                placeholder="Observacoes adicionais sobre a venda..."
                rows={3}
                className="resize-none"
              />
            </div>

            {/* ACTION BUTTONS - Mobile only */}
            <div className="flex justify-end gap-3 lg:hidden pb-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
                data-testid="btn-cancelar-mobile"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="btn-cadastrar-mobile"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Cadastrar Venda
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* ===== RIGHT COLUMN: SUMMARY ===== */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-resumo">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-1 h-5 bg-red-600 rounded-full" />
                  <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Resumo</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Cliente</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5" data-testid="resumo-cliente">
                      {formData.cliente.nome_razao_social || '\u2014'}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">{formData.cliente.tipo_documento}</p>
                    <p className="text-sm text-gray-700 mt-0.5" data-testid="resumo-documento">
                      {formData.cliente.documento || '\u2014'}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Endereco</p>
                    <p className="text-sm text-gray-700 mt-0.5" data-testid="resumo-endereco">
                      {formData.endereco.endereco
                        ? `${formData.endereco.endereco}${formData.endereco.numero ? ', ' + formData.endereco.numero : ''} - ${formData.endereco.cidade || ''}/${formData.endereco.estado || ''}`
                        : '\u2014'}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Empresa</p>
                    <p className="text-sm text-gray-700 mt-0.5" data-testid="resumo-empresa">
                      {empresas.find(e => e.id === formData.empresa_id)?.nome || '\u2014'}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Tipo</p>
                    <p className="text-sm text-gray-700 mt-0.5">{formData.tipo_venda}</p>
                  </div>

                  <div className="border-t border-gray-100" />

                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Planos Selecionados</p>
                    {(() => {
                      const planos = [];
                      ['tv', 'internet', 'fixo', 'movel', 'alacarte', 'pontosextra', 'pontosadicionais'].forEach(key => {
                        if (formData[`plano_${key}_nome`]) {
                          planos.push({ nome: formData[`plano_${key}_nome`], valor: formData[`plano_${key}_valor`], qtd: formData[`plano_${key}_qtd`] || 1 });
                        }
                      });
                      planos.push(...planosExtrasResumo(formData));
                      if (planos.length === 0) return <p className="text-sm text-gray-400 italic">Nenhum plano selecionado</p>;
                      return planos.map((p, i) => (
                        <div key={i} className="flex justify-between text-sm mb-1.5">
                          <span className="text-gray-700 truncate mr-2">{p.nome}{p.qtd > 1 ? ` x${p.qtd}` : ''}</span>
                          <span className="font-medium text-gray-800 whitespace-nowrap">R$ {(p.valor * p.qtd).toFixed(2)}</span>
                        </div>
                      ));
                    })()}
                  </div>

                  <div className="border-t border-gray-100" />

                  <div className="flex justify-between">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Pagamento</p>
                      <p className="text-sm text-gray-700 mt-0.5">
                        {formData.pagamento.forma_pagamento === 'Débito' ? 'Debito Automatico' : formData.pagamento.forma_pagamento}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Vencimento</p>
                      <p className="text-sm text-gray-700 mt-0.5">Dia {formData.pagamento.vencimento_fatura}</p>
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-[10px] text-red-400 uppercase tracking-wider font-bold">Valor Total</p>
                    <p className="text-2xl font-bold text-red-600 mt-1" data-testid="resumo-valor-total">
                      R$ {valorTotal.toFixed(2)}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] text-gray-500">Formulario {progresso}% completo</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden" data-testid="progress-bar">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          progresso === 100 ? 'bg-green-500' : progresso >= 60 ? 'bg-amber-500' : 'bg-red-400'
                        }`}
                        style={{ width: `${progresso}%` }}
                      />
                    </div>
                    {faltam.length > 0 && (
                      <p className="text-[10px] text-gray-400 mt-1.5">
                        Faltam {faltam.length} campo(s): {faltam.slice(0, 3).join(', ')}{faltam.length > 3 ? '...' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Desktop Action Buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.history.back()}
                  data-testid="btn-cancelar"
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="btn-cadastrar"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cadastrando...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Cadastrar Venda
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Dialog Duplicidade */}
      <Dialog open={dupDialogOpen} onOpenChange={setDupDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className={`h-6 w-6 ${dupInfo?.bloqueado ? 'text-red-600' : 'text-amber-500'}`} />
              {dupInfo?.bloqueado ? 'Venda Bloqueada' : 'Alerta de Duplicidade'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${dupInfo?.bloqueado ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
              <p className={`font-semibold ${dupInfo?.bloqueado ? 'text-red-800' : 'text-amber-800'}`}>
                {dupInfo?.mensagem}
              </p>
            </div>
            {dupInfo?.vendas_encontradas?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Vendas encontradas:</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dupInfo.vendas_encontradas.map((v, i) => (
                    <div key={i} className="p-2 bg-gray-50 rounded border text-sm">
                      <span className="font-semibold">#{v.protocolo}</span>
                      {' - '}{v.cliente?.nome_razao_social || v.vendedor_nome}
                      {' - '}<span className="text-gray-500">{v.status}</span>
                      {v.criado_em && <span className="text-gray-400 ml-1">({v.criado_em?.slice(0,10).split('-').reverse().join('/')})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDupDialogOpen(false); setForcarEnvio(false); }}>
              Cancelar
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={async () => {
              setDupDialogOpen(false);
              setForcarEnvio(true);
              setLoading(true);
              try {
                const dadosEnviar = { ...formData, valor_plano: valorTotal, venda_duplicada: true };
                await vendasAPI.create(dadosEnviar);
                toast.success('Venda enviada! Aguardando liberacao do BKO/Admin.');
                if (pendingBlock && triggerPendingBlock) { triggerPendingBlock(); }
                setFormData({
                  telefone_venda: '', data_prevista_vendedor: '', turno_previsto_vendedor: '', modalidade_venda: 'Proposta', contrato_cliente_base: '',
                  numero_contrato_cancelamento: '', nome_titular_antigo: '', telefone_titular_antigo: '', email_titular_antigo: '', cpf_cnpj_titular_antigo: '',
                  cliente: { tipo_documento: 'CPF', documento: '', nome_razao_social: '', email: '', telefone1: '', telefone2: '', nome_mae: '', rg: '', data_nascimento: '' },
                  endereco: { cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', hp: '' },
                  produtos: [], tipo_venda: 'Base', categoria: 'Residencial', valor_plano: '', empresa_id: '',
                  pagamento: { forma_pagamento: 'Débito', vencimento_fatura: '10', banco: '', agencia: '', conta: '' },
                  observacoes_campo: ''
                });
                setForcarEnvio(false);
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Erro ao cadastrar venda');
              } finally {
                setLoading(false);
              }
            }} data-testid="btn-enviar-duplicada">
              Enviar Mesmo Assim (BKO/Admin libera)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NovaVenda;
